import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import KenbrightLogo from '../assets/images/KenbrightLogo';

const MFAVerificationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setSession } = useAuth();

    const username = location.state?.username;
    const mfaMethod = location.state?.mfaMethod;

    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(300);
    const [canResend, setCanResend] = useState(false);
    const inputRefs = useRef([]);

    // Redirect to login if no state
    useEffect(() => {
        if (!username) {
            navigate('/login', { replace: true });
        }
    }, [username, navigate]);

    // Countdown timer
    useEffect(() => {
        if (canResend) return;

        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    setCanResend(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [canResend]);

    // Focus first input on mount
    useEffect(() => {
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }, []);

    const handleOtpChange = (index, value) => {
        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all 6 digits entered
        if (value && index === 5 && newOtp.every(d => d !== '')) {
            handleVerify(newOtp.join(''));
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowRight' && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();
        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split('');
            setOtp(digits);
            inputRefs.current[5]?.focus();
            setTimeout(() => handleVerify(pastedData), 100);
        }
    };

    const handleVerify = async (code) => {
        if (code.length !== 6 || loading) return;
        setLoading(true);
        setError('');

        try {
            const result = await authService.mfaVerify(username, code);
            const loginData = result.data || result;

            if (loginData.token) {
                setSession(loginData);
                navigate('/dashboard', { replace: true });
            } else {
                setError('Invalid response from server');
            }
        } catch (err) {
            setError(err.message || 'Invalid verification code');
            setOtp(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleVerify(otp.join(''));
    };

    const handleResendOtp = async () => {
        if (!canResend) return;
        setCanResend(false);
        setCountdown(300);
        setError('');
        try {
            await authService.mfaResend(username);
        } catch {
            // Silently handle resend errors
        }
    };

    const getMethodDescription = () => {
        if (mfaMethod === 'EMAIL_AND_SMS') {
            return 'A verification code has been sent to your email and phone';
        }
        if (mfaMethod === 'SMS') {
            return 'A verification code has been sent to your phone';
        }
        return 'A verification code has been sent to your email';
    };

    const isOtpComplete = otp.every(d => d !== '');

    if (!username) return null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md relative z-10">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-60 h-48">
                            <KenbrightLogo className="w-full h-full" />
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="text-center mb-4">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-full mb-3">
                                <Shield className="w-6 h-6 text-blue-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900">Two-Factor Authentication</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {getMethodDescription()}
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                <span className="text-sm">{error}</span>
                            </div>
                        )}

                        <div className="flex justify-center gap-2">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={el => inputRefs.current[index] = el}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    onPaste={index === 0 ? handleOtpPaste : undefined}
                                    disabled={loading}
                                    className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl transition-colors
                                        ${digit ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                                        focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20
                                        disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                            ))}
                        </div>

                        <button
                            type="submit"
                            disabled={!isOtpComplete || loading}
                            className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <RefreshCw className="w-5 h-5 animate-spin" />
                                    Verifying...
                                </>
                            ) : (
                                'Verify & Sign In'
                            )}
                        </button>

                        <div className="text-center">
                            {canResend ? (
                                <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    className="text-sm text-blue-600 hover:underline"
                                >
                                    Resend code
                                </button>
                            ) : (
                                <p className="text-sm text-gray-500">
                                    Resend code in <span className="font-medium">{Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}</span>
                                </p>
                            )}
                        </div>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => navigate('/login', { replace: true })}
                                className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 mx-auto"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Use a different account
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MFAVerificationPage;
